import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Pourquoi() {
  return (
    <div className="pourquoi-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="section-container">
          <span className="section-label">Positionnement</span>
          <h1 className="page-title">Pourquoi Open Cortex</h1>
          <p className="page-subtitle">
            Structurer avant d'automatiser. Clarifier avant d'outiller. Décider sur des bases solides pour accélérer la croissance sans perdre le contrôle.
          </p>
          <p className="hero-description">
            Open Cortex accompagne les organisations qui veulent fiabiliser leurs opérations, gagner en lisibilité et intégrer l'automatisation et l'IA de manière pertinente, sans transformation inutilement brutale.
          </p>
          <div className="hero-actions">
            <a href="https://calendly.com/open-cortex/farid-amadet" target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary">
                Prendre RDV <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Ce que vous y gagnez concrètement</h2>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h3>Plus de leads qualifiés et une conversion facilitée grâce à une organisation commerciale plus lisible</h3>
            </div>
            <div className="benefit-card">
              <h3>Du temps récupéré sur les tâches répétitives et les processus perfectibles</h3>
            </div>
            <div className="benefit-card">
              <h3>Une visibilité opérationnelle renforcée via des tableaux de bord utiles</h3>
            </div>
            <div className="benefit-card">
              <h3>Des décisions plus rapides parce que l'information circule mieux</h3>
            </div>
            <div className="benefit-card">
              <h3>Une organisation capable d'absorber la croissance avec fluidité</h3>
            </div>
            <div className="benefit-card">
              <h3>Des automatisations et de l'IA qui servent réellement le business</h3>
            </div>
            <div className="benefit-card">
              <h3>Moins de dépendance à des systèmes complexes ou difficiles à maintenir</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Section */}
      <section className="positioning-section">
        <div className="section-container">
          <div className="positioning-content">
            <h2>Notre approche</h2>
            <div className="positioning-grid">
              <div className="positioning-block">
                <h3>Open Cortex n'est pas</h3>
                <ul className="positioning-list">
                  <li>Un vendeur de logiciel</li>
                  <li>Une agence qui empile des outils</li>
                  <li>Un cabinet éloigné des réalités opérationnelles</li>
                </ul>
              </div>
              <div className="positioning-block highlight">
                <h3>Open Cortex est</h3>
                <ul className="positioning-list">
                  <li>Un partenaire opérationnel</li>
                  <li>Qui structure d'abord le réel</li>
                  <li>Puis automatise si pertinent</li>
                  <li>Et intègre l'IA là où elle crée un avantage concret</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Contrast Section */}
      <section className="contrast-section">
        <div className="section-container">
          <div className="contrast-content">
            <h2>Pourquoi pas une solution standard ?</h2>
            <div className="contrast-points">
              <div className="contrast-point">
                <p>Un outil seul ne suffit pas à structurer une organisation. Sans clarification préalable du fonctionnement réel, l'outil risque de figer des processus encore en construction.</p>
              </div>
              <div className="contrast-point">
                <p>Automatiser un fonctionnement encore en cours de structuration peut amplifier certaines difficultés. La rigueur de la phase amont conditionne la qualité du résultat.</p>
              </div>
              <div className="contrast-point">
                <p>L'IA est un levier puissant lorsqu'elle s'appuie sur des bases claires. Intégrée sur des processus flous, elle génère de la complexité plutôt que de la valeur.</p>
              </div>
              <div className="contrast-point">
                <p>Le vrai gain vient d'une organisation cohérente avant la technologie. L'automatisation et l'IA amplifient ce qui existe : mieux vaut amplifier une base solide.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Method Summary Section */}
      <section className="method-summary-section">
        <div className="section-container">
          <div className="method-summary-content">
            <h2>Notre méthode en résumé</h2>
            <div className="method-steps">
              <div className="method-step">
                <div className="step-number">1</div>
                <div className="step-text">
                  <h3>Comprendre le fonctionnement réel</h3>
                  <p>Observation directe des opérations, identification des flux critiques et des dépendances.</p>
                </div>
              </div>
              <div className="method-step">
                <div className="step-number">2</div>
                <div className="step-text">
                  <h3>Structurer les processus clés</h3>
                  <p>Formalisation rigoureuse, documentation exploitable, clarification des responsabilités.</p>
                </div>
              </div>
              <div className="method-step">
                <div className="step-number">3</div>
                <div className="step-text">
                  <h3>Automatiser et intégrer l'IA quand pertinent</h3>
                  <p>Workflows ciblés, aide à la décision, coordination opérationnelle, gains mesurables.</p>
                </div>
              </div>
              <div className="method-step">
                <div className="step-number">4</div>
                <div className="step-text">
                  <h3>Transférer les compétences pour plus d'autonomie</h3>
                  <p>Formation, documentation de référence, capacité à maintenir et faire évoluer.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Parlons de votre contexte</h2>
            <p>Un échange simple pour comprendre votre situation et voir si un accompagnement Open Cortex peut vous aider concrètement.</p>
            <a href="https://calendly.com/open-cortex/farid-amadet" target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary btn-large">
                Prendre RDV <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
